import { useMemo, useState } from 'react';
import { COLS } from '../constants';
import { ColumnFilters, ColSort, ElecDiffRow, ElecRowVals, TypeFilter } from '../types';
import { getRowVals } from '../utils/compareElec';
import ColumnFilterDropdown from './ColumnFilterDropdown';

const TYPE_FILTERS: TypeFilter[] = ['전체', '변경', '추가', '삭제', '일치'];

const matchesTypeFilter = (row: ElecDiffRow, f: TypeFilter): boolean => {
  if (f === '전체') return true;
  if (f === '변경') return row.difTpCd === 'CHG';
  if (f === '추가') return row.difTpCd === 'ADD';
  if (f === '삭제') return row.difTpCd === 'DEL';
  return row.difTpCd === 'SAME';
};

interface Props {
  title: string;
  rows: ElecDiffRow[];
  onClose: () => void;
}

/**
 * 차단기 카드 클릭 시 뜨는 상세 팝업 — 메인 상세표(ElecDetailTable)와 같은 필터/정렬/열숨김
 * UI를 갖지만, 모달 전용 독립 상태(로컬 useState)로 동작한다 (메인 표 상태에 영향 없음).
 * HTML 툴의 showBrkModal()/modalRender() 포팅.
 */
const BreakerDetailModal = ({ title, rows, onClose }: Props) => {
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('전체');
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});
  const [sort, setSortState] = useState<ColSort>({ col: null, dir: 'asc' });
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(new Set());

  const typeFiltered = useMemo(
    () => rows.filter((r) => matchesTypeFilter(r, typeFilter)),
    [rows, typeFilter]
  );

  const filteredExcluding = (excludeCol?: keyof ElecRowVals) => {
    let list = typeFiltered;
    for (const [col, vals] of Object.entries(columnFilters)) {
      if (col === excludeCol || !vals || vals.size === 0) continue;
      list = list.filter((r) => vals.has(String(getRowVals(r)[col as keyof ElecRowVals])));
    }
    return list;
  };

  const filtered = useMemo(() => filteredExcluding(), [typeFiltered, columnFilters]);

  const sorted = useMemo(() => {
    if (!sort.col) return filtered;
    const col = sort.col;
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((ra, rb) => {
      const va = getRowVals(ra)[col];
      const vb = getRowVals(rb)[col];
      const na = typeof va === 'number' ? va : parseFloat(String(va));
      const nb = typeof vb === 'number' ? vb : parseFloat(String(vb));
      if (!Number.isNaN(na) && !Number.isNaN(nb) && String(va) !== '' && String(vb) !== '') {
        return (na - nb) * factor;
      }
      return String(va).localeCompare(String(vb), 'ko') * factor;
    });
  }, [filtered, sort]);

  const visibleCols = COLS.filter((c) => !hiddenCols.has(c.id));

  const toggleHiddenCol = (colId: string) =>
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(colId)) next.delete(colId);
      else next.add(colId);
      return next;
    });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal brk-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <span className="meta">
            {sorted.length}건 / 전체 {rows.length}건
          </span>
          <button type="button" className="btn-close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="filter-bar">
          {TYPE_FILTERS.map((t) => (
            <button
              key={t}
              type="button"
              className={`filter-btn f-${t}${typeFilter === t ? ' active' : ''}`}
              onClick={() => setTypeFilter(t)}
            >
              {t}
            </button>
          ))}
          <button
            type="button"
            className="btn-clear-all"
            onClick={() => {
              setColumnFilters({});
              setSortState({ col: null, dir: 'asc' });
              setTypeFilter('전체');
            }}
          >
            🔄 필터 초기화
          </button>
        </div>
        <div className="col-ctrl-bar">
          <span>열 표시/숨기기:</span>
          {COLS.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`col-toggle${hiddenCols.has(c.id) ? ' hid' : ''}`}
              onClick={() => toggleHiddenCol(c.id)}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div className="modal-body tbl-wrap">
          <table className="result">
            <thead>
              <tr>
                {visibleCols.map((c) => (
                  <ColumnFilterDropdown
                    key={c.id}
                    label={c.label}
                    width={c.width}
                    align={c.align}
                    thClassName={
                      c.group === 'loc' ? 'th-loc' : c.group === 'elec' ? 'th-elec' : undefined
                    }
                    selected={columnFilters[c.id]}
                    sortDir={sort.col === c.id ? sort.dir : null}
                    getAllValues={() =>
                      Array.from(
                        new Set(filteredExcluding(c.id).map((r) => String(getRowVals(r)[c.id])))
                      ).sort((a, b) => a.localeCompare(b, 'ko'))
                    }
                    onApplyFilter={(values) =>
                      setColumnFilters((prev) => {
                        const next = { ...prev };
                        if (values === null) delete next[c.id];
                        else next[c.id] = values;
                        return next;
                      })
                    }
                    onSetSort={(dir) =>
                      setSortState(dir === null ? { col: null, dir: 'asc' } : { col: c.id, dir })
                    }
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={visibleCols.length} className="empty">
                    해당 데이터가 없습니다
                  </td>
                </tr>
              ) : (
                sorted.map((row) => {
                  const v = getRowVals(row);
                  const rowCls =
                    row.difTpCd === 'SAME'
                      ? 'r-same'
                      : row.difTpCd === 'ADD'
                      ? 'r-add'
                      : row.difTpCd === 'DEL'
                      ? 'r-del'
                      : 'r-chg';
                  const badgeCls =
                    row.difTpCd === 'SAME'
                      ? 'badge-same'
                      : row.difTpCd === 'ADD'
                      ? 'badge-add'
                      : row.difTpCd === 'DEL'
                      ? 'badge-del'
                      : 'badge-chg';
                  return (
                    <tr key={row.key} className={rowCls}>
                      {visibleCols.map((c) => {
                        if (c.id === 'difTpCtn') {
                          return (
                            <td key={c.id} style={{ textAlign: c.align }}>
                              <span className={`badge ${badgeCls}`} title={row.difTpCtn}>
                                {row.difTpCtn}
                              </span>
                            </td>
                          );
                        }
                        const value = v[c.id];
                        const text = value === '' || value == null ? '-' : String(value);
                        return (
                          <td key={c.id} style={{ textAlign: c.align }} title={text}>
                            {text}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BreakerDetailModal;
